import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Avatar, Badge, Button, Card, Input, Select, StatusPill, Toast } from "../components";
import { Icon, useLucideIcons } from "../components/Icon";
import type { ClassHourStatus } from "../components/domain/StatusPill";

type MobileTab = "hours" | "schedule" | "me";

interface ClassRecord {
  date: string;
  time: string;
  subject: string;
  hours: number;
  status: ClassHourStatus;
}

interface UpcomingClass {
  day: string;
  date: string;
  time: string;
  subject: string;
  teacher: string;
}

const M_DATA = {
  remain: 12.5,
  monthTaught: 22.5,
  monthTotal: 35,
  records: [
    { date: "6月12日 周四", time: "16:00–17:30", subject: "数学 · 一对一", hours: 2, status: "upcoming" },
    { date: "6月10日 周二", time: "16:00–17:30", subject: "数学 · 一对一", hours: 2, status: "taught" },
    { date: "6月9日 周一", time: "16:00–17:30", subject: "数学 · 一对一", hours: 2, status: "taught" },
    { date: "6月7日 周六", time: "10:00–11:30", subject: "数学 · 一对一", hours: 2, status: "leave" },
    { date: "6月5日 周四", time: "16:00–17:30", subject: "数学 · 一对一", hours: 2, status: "taught" },
  ] as ClassRecord[],
  upcoming: [
    { day: "今天", date: "6/12", time: "16:00", subject: "数学 · 一对一", teacher: "王老师" },
    { day: "周五", date: "6/13", time: "16:00", subject: "数学 · 一对一", teacher: "王老师" },
    { day: "下周一", date: "6/16", time: "16:00", subject: "数学 · 一对一", teacher: "王老师" },
  ] as UpcomingClass[],
};

// ── Mini calendar for schedule tab ─────────────────────────────────────

const WEEK_LABELS = ["一", "二", "三", "四", "五", "六", "日"];
const MONTH_NAMES = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];

function getDaysInMonth(y: number, m: number) { return new Date(y, m + 1, 0).getDate(); }
function getFirstDow(y: number, m: number) { return (new Date(y, m, 1).getDay() + 6) % 7; }

// Fake class events for the mobile calendar view
const MOCK_EVENTS: Record<string, string> = {
  "2026-8-12": "数学 16:00",
  "2026-8-14": "数学 16:00",
  "2026-8-15": "数学 10:00",
  "2026-8-18": "数学 16:00",
  "2026-8-20": "数学 16:00",
  "2026-8-22": "数学 10:00",
};

function MobileCalendar() {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState(today.getDate());

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDow = getFirstDow(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
    setSelectedDay(1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
    setSelectedDay(1);
  };

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedKey = `${viewYear}-${viewMonth + 1}-${selectedDay}`;
  const selectedEvent = MOCK_EVENTS[selectedKey];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "4px 16px 16px" }}>
      <Card pad={false}>
        {/* Month nav */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 8px" }}>
          <button onClick={prevMonth} style={calNavBtn}>
            <Icon name="chevron-left" size={18} />
          </button>
          <span style={{ fontWeight: 800, fontSize: 15, color: "var(--text-strong)" }}>
            {viewYear}年{MONTH_NAMES[viewMonth]}
          </span>
          <button onClick={nextMonth} style={calNavBtn}>
            <Icon name="chevron-right" size={18} />
          </button>
        </div>

        {/* Weekday headers */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", padding: "0 12px", textAlign: "center" }}>
          {WEEK_LABELS.map((d, i) => (
            <div key={d} style={{ fontSize: 11, fontWeight: 700, color: i >= 5 ? "var(--accent)" : "var(--text-subtle)", padding: "4px 0" }}>
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", padding: "2px 12px 14px", textAlign: "center" }}>
          {cells.map((day, i) => {
            if (!day) return <div key={`e${i}`} />;
            const isToday = day === today.getDate() && viewYear === today.getFullYear() && viewMonth === today.getMonth();
            const isSel = day === selectedDay;
            const eventKey = `${viewYear}-${viewMonth + 1}-${day}`;
            const hasEvent = !!MOCK_EVENTS[eventKey];
            return (
              <button
                key={day}
                onClick={() => setSelectedDay(day)}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: "2px 0 6px",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: isSel || isToday ? 800 : 400,
                    background: isSel ? "var(--brand)" : "transparent",
                    color: isSel ? "#fff" : isToday ? "var(--brand)" : "var(--text-body)",
                    outline: isToday && !isSel ? "1.5px solid var(--brand)" : "none",
                    outlineOffset: -1,
                  }}
                >
                  {day}
                </span>
                {hasEvent && (
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: isSel ? "#fff" : "var(--brand)", flexShrink: 0 }} />
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Selected day event */}
      <Card title={`${viewMonth + 1}月${selectedDay}日 课程安排`}>
        {selectedEvent ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: "var(--radius-md)", background: "var(--brand-subtle)" }}>
            <div style={{ width: 4, alignSelf: "stretch", borderRadius: 4, background: "var(--brand)" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-strong)" }}>数学 · 一对一</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{selectedEvent} · 王老师</div>
            </div>
            <Badge tone="brand">2 课时</Badge>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "16px 0", color: "var(--text-subtle)", fontSize: 13 }}>
            当天暂无课程安排
          </div>
        )}
      </Card>
    </div>
  );
}

const calNavBtn: CSSProperties = {
  border: "none",
  background: "var(--surface-sunken)",
  borderRadius: "var(--radius-sm)",
  padding: "4px 8px",
  cursor: "pointer",
  color: "var(--text-muted)",
  display: "flex",
  alignItems: "center",
};

// ── Main component ──────────────────────────────────────────────────────

export function MobileApp() {
  const [tab, setTab] = useState<MobileTab>("hours");
  const [sheet, setSheet] = useState(false);
  const [toast, setToast] = useState(false);
  useLucideIcons([tab, sheet, toast]);

  const pct = Math.round((M_DATA.monthTaught / M_DATA.monthTotal) * 100);

  const HoursTab = (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "4px 16px 16px" }}>
      <div
        style={{
          background: "linear-gradient(150deg, var(--teal-600), var(--teal-800))",
          borderRadius: "var(--radius-xl)",
          padding: 22,
          color: "#fff",
          boxShadow: "var(--shadow-brand)",
        }}
      >
        <div style={{ fontSize: 13, opacity: 0.9, fontWeight: 600 }}>剩余课时</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
          <span
            style={{
              fontFamily: "var(--font-num)",
              fontWeight: 800,
              fontSize: 44,
              lineHeight: 1,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {M_DATA.remain}
          </span>
          <span style={{ fontSize: 16, opacity: 0.9 }}>课时</span>
        </div>
        <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", fontSize: 12, whiteSpace: "nowrap" }}>
          <span style={{ opacity: 0.9 }}>本月已上 {M_DATA.monthTaught} 课时</span>
          <span style={{ opacity: 0.9 }}>{pct}%</span>
        </div>
        <div style={{ marginTop: 6, height: 6, borderRadius: 999, background: "rgba(255,255,255,.25)" }}>
          <div style={{ width: pct + "%", height: "100%", borderRadius: 999, background: "#fff" }} />
        </div>
      </div>

      <Button variant="accent" block iconLeft={<Icon name="calendar-x" size={18} />} onClick={() => setSheet(true)}>
        申请请假
      </Button>

      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "4px 2px 10px" }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: "var(--text-strong)" }}>课时记录</span>
          <span style={{ fontSize: 13, color: "var(--brand)", fontWeight: 600 }}>全部</span>
        </div>
        <Card pad={false}>
          {M_DATA.records.map((r, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "13px 14px",
                borderTop: i ? "1px solid var(--divider)" : "none",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong)" }}>{r.date}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {r.time} · {r.subject}
                </div>
              </div>
              <span
                style={{
                  fontFamily: "var(--font-num)",
                  fontWeight: 800,
                  fontVariantNumeric: "tabular-nums",
                  color: r.status === "leave" ? "var(--status-leave-fg)" : "var(--text-strong)",
                }}
              >
                {r.status === "leave" ? "−" : "+"}
                {r.hours}
              </span>
              <StatusPill status={r.status} dot={false} />
            </div>
          ))}
        </Card>
      </div>
    </div>
  );

  const MeTab = (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "4px 16px 16px" }}>
      <Card style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <Avatar name="王雪琴" size="lg" ring />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text-strong)" }}>王雪琴</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>三年级 · WELL-E</div>
        </div>
        <Badge tone="brand" dot>
          在读
        </Badge>
      </Card>
      <Card pad={false}>
        {(
          [
            ["user", "个人资料"],
            ["bell", "课时提醒"],
            ["file-text", "请假记录"],
            ["credit-card", "缴费记录"],
            ["headphones", "联系老师"],
          ] as const
        ).map(([icon, label], i) => (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: 14,
              borderTop: i ? "1px solid var(--divider)" : "none",
            }}
          >
            <span style={{ color: "var(--brand)", display: "flex" }}>
              <Icon name={icon} size={19} />
            </span>
            <span style={{ flex: 1, fontSize: 15, color: "var(--text-body)", fontWeight: 500 }}>{label}</span>
            <span style={{ color: "var(--text-subtle)", display: "flex" }}>
              <Icon name="chevron-right" size={18} />
            </span>
          </div>
        ))}
      </Card>
    </div>
  );

  const tabs: { k: MobileTab; label: string; icon: string }[] = [
    { k: "hours", label: "课时", icon: "timer" },
    { k: "schedule", label: "日历", icon: "calendar-days" },
    { k: "me", label: "我的", icon: "user" },
  ];
  const titles: Record<MobileTab, string> = { hours: "我的课时", schedule: "上课日历", me: "我的" };

  const tabContent = tab === "hours" ? HoursTab : tab === "schedule" ? <MobileCalendar /> : MeTab;

  return (
    <PhoneFrame>
      {/* App header */}
      <div
        style={{
          padding: "8px 18px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: "var(--text-subtle)" }}>你好，王雪琴的家长</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text-strong)" }}>{titles[tab]}</div>
        </div>
        <div style={{ position: "relative" }}>
          <span style={{ display: "flex", color: "var(--text-muted)" }}>
            <Icon name="bell" size={22} />
          </span>
          <span
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 8,
              height: 8,
              borderRadius: 999,
              background: "var(--accent)",
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", background: "var(--surface-page)" }}>{tabContent}</div>

      {/* Tab bar */}
      <div
        style={{
          display: "flex",
          borderTop: "1px solid var(--border-subtle)",
          background: "var(--surface-card)",
          padding: "8px 0 env(safe-area-inset-bottom, 6px)",
          flexShrink: 0,
        }}
      >
        {tabs.map((t) => {
          const on = tab === t.k;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              style={{
                flex: 1,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                padding: "4px 0",
                color: on ? "var(--brand)" : "var(--text-subtle)",
                fontWeight: on ? 700 : 500,
              }}
            >
              <Icon name={t.icon} size={23} />
              <span style={{ fontSize: 11 }}>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Leave request sheet */}
      {sheet && (
        <div
          onClick={() => setSheet(false)}
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(33,30,19,.42)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "flex-end",
            zIndex: 20,
            borderRadius: "inherit",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              background: "var(--surface-card)",
              borderRadius: "24px 24px 0 0",
              padding: "10px 18px calc(env(safe-area-inset-bottom, 16px) + 12px)",
              animation: "ji-sheet .32s cubic-bezier(.34,1.4,.5,1)",
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                borderRadius: 999,
                background: "var(--border-default)",
                margin: "0 auto 16px",
              }}
            />
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text-strong)", marginBottom: 4 }}>申请请假</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
              请假课时将自动从剩余课时中扣减
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Select
                label="请假日期"
                defaultValue="d1"
                options={[
                  { value: "d1", label: "6月14日 周六 10:00–11:30" },
                  { value: "d2", label: "6月16日 周一 16:00–17:30" },
                ]}
              />
              <Input label="请假事由" placeholder="如：身体不适 / 家中有事" />
              <Button
                variant="primary"
                block
                onClick={() => {
                  setSheet(false);
                  setToast(true);
                }}
              >
                提交申请
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: "absolute", left: 16, right: 16, bottom: 92, zIndex: 30 }}>
          <Toast tone="success" title="请假已提交" onClose={() => setToast(false)}>
            待王老师审批 · 预计扣减 2 课时
          </Toast>
        </div>
      )}
    </PhoneFrame>
  );
}

// ── PhoneFrame: desktop shows phone shell, mobile shows full-screen ─────

function PhoneFrame({ children }: { children: ReactNode }) {
  const [isNativeMobile, setIsNativeMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 500
  );

  useEffect(() => {
    const check = () => setIsNativeMobile(window.innerWidth < 500);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  if (isNativeMobile) {
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100dvh",
          background: "var(--surface-card)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Native status bar safe area */}
        <div style={{ height: "env(safe-area-inset-top, 0px)", flexShrink: 0 }} />
        {children}
      </div>
    );
  }

  // Desktop: render inside phone frame mockup
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: 20,
        background: "var(--surface-sunken)",
      }}
    >
      <div
        style={{
          width: 390,
          height: 844,
          background: "#000",
          borderRadius: 50,
          padding: 7,
          boxShadow: "var(--shadow-xl), 0 0 0 1px rgba(0,0,0,.15)",
          flex: "none",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            background: "var(--surface-card)",
            borderRadius: 44,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Status bar */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "14px 26px 6px",
              fontSize: 14,
              fontWeight: 700,
              color: "var(--text-strong)",
              fontFamily: "var(--font-num)",
              flexShrink: 0,
            }}
          >
            <span>9:41</span>
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Icon name="signal" size={16} />
              <Icon name="wifi" size={16} />
              <Icon name="battery-full" size={20} />
            </span>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
